import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PencilIcon} from '@primer/octicons-react'
import {IconButton, PageHeader, useResponsiveValue} from '@primer/react'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {PullRequestBanners} from './PullRequestBanners'
import {FilesChangedFeedback} from './FilesChangedFeedback'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import {PullRequestEditTitleForm} from './PullRequestEditTitleForm'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import {StackState} from './StackState'
import type {AliveChannels, HeaderPageData} from '../page-data/payloads/header'
import {PullRequestHeaderNavigation} from './PullRequestHeaderNavigation'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {DiffStat, DiffStatLoadingSkeleton} from './DiffStat'
import {clsx} from 'clsx'
import styles from './PullRequestHeader.module.css'
import {isLoggedIn} from '@github-ui/client-env'
import {MergeMethodContextProvider, MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {isPullRequestOpenState} from '../utils/pull-request-state'
import {StickyPullRequestHeader, type StickyPullRequestHeaderProps} from './StickyPullRequestHeader'
import {setTitle} from '@github-ui/document-metadata'
import {PullRequestsTopBanners} from './banners/PullRequestsTopBanners'
import {DockedPanel, useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeStatusPanel} from './toolbar/MergeStatusPanel'
import {OverviewInfoPanel, OverviewTimelinePanel} from './toolbar/OverviewInfoPanel'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import {getRouteConfig} from '../routes/route-manager'

export type PullRequestHeaderProps = HeaderPageData & {
  activeRoute: PullRequestActiveRoute
  stickyHeaderVariant?: StickyPullRequestHeaderProps['pageVariant']
  aliveChannels: AliveChannels
}

/**
 * LivePullRequestHeader is a thin wrapper around PullRequestHeader that calls `useHeaderPageData` to automatically
 * update the header when the alive channel is triggered with `{title_updated: true}`, `{git_updated: true}`, or when
 * there is an update to the PullRequestState channel. This lives here instead of the base page so that only the header
 * re-renders on cache updates, not the entire page.
 */
export function LivePullRequestHeader({
  aliveChannels,
  bannersData,
  activeRoute,
  stickyHeaderVariant,
  pullRequest,
  pageTitle,
  repository,
  urls,
  user,
  mergeStatusButtonData,
  stack,
  viewSettings,
}: PullRequestHeaderProps) {
  const {data: headerData} = useHeaderPageData({
    aliveChannels,
    bannersData,
    pageTitle,
    pullRequest,
    repository,
    stack,
    urls,
    user,
    mergeStatusButtonData,
    viewSettings,
  })
  return <PullRequestHeader {...headerData} activeRoute={activeRoute} stickyHeaderVariant={stickyHeaderVariant} />
}

export function PullRequestHeader({
  bannersData,
  activeRoute,
  stickyHeaderVariant,
  pullRequest,
  repository,
  pageTitle,
  urls,
  user,
  mergeStatusButtonData,
  stack,
}: PullRequestHeaderProps) {
  const {dockedPanelSupported} = getRouteConfig(activeRoute)
  const [isEditing, setIsEditing] = useState(false)
  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')

  const shouldShowLoggedInOptions = isLoggedIn()
  const isPROpen = isPullRequestOpenState(pullRequest.state)
  const hasMergeStatusButton = isFeatureEnabled('pull_request_merge_status_button')
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const headerButtonSize = hasMergeStatusButton ? 'medium' : 'small'
  const hasValidatedMergeData = validatedMergeData !== null

  const sidePanelOpen = useIsDockedPanelOpen()

  // Docked merge status panel — only active on files/changes routes
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  // Auto-close the docked merge status panel when the PR is merged or closed.
  // After merge, validatedMergeData becomes null which unmounts MergeStatusButton
  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  useEffect(() => {
    if (pageTitle) {
      setTitle(pageTitle)
    }
  }, [pageTitle])

  const feedbackRenderLocation = useResponsiveValue({narrow: 'mobile', regular: 'desktop'}, 'desktop') as
    | 'mobile'
    | 'desktop'

  const isFilesChangedRoute = activeRoute === 'files_changed'

  return (
    <>
      {isDockedMode && isFeatureEnabled('prs_overview_panel') && (
        <>
          <OverviewInfoPanel />
          <OverviewTimelinePanel />
        </>
      )}
      {/** Used by suggested-changes.ts to disable the "suggested changes" UI when the PR is closed */}
      <div className="js-pull-header-details" data-pull-is-open={isPROpen ? 'true' : 'false'} hidden />
      <PullRequestsTopBanners bannersData={bannersData} urls={urls} activeRoute={activeRoute} />
      {isEditing && (
        <div className={clsx(showUpdatedHeader && 'tmp-mt-3')}>
          <PullRequestEditTitleForm
            initialTitle={pullRequest.title}
            pullRequestNumber={pullRequest.number}
            onCloseForm={() => setIsEditing(false)}
          />
        </div>
      )}
      <PageHeader
        className={clsx(
          'flex-items-center',
          showUpdatedHeader && 'tmp-mt-3',
          sidePanelOpen && styles.sidePanelOpen,
          sidePanelOpen && showUpdatedHeader && styles.sidePanelOpenUpdatedHeader,
          styles.actionsAboveTitleOnNarrow,
          styles.actionsAboveTitleOnNarrowContainer,
        )}
      >
        {isEditing && (
          // this is done so that we still have an h1 element present when editing the title
          <h1 className="sr-only">{`${pullRequest.title} - #${pullRequest.number}`}</h1>
        )}
        {!isEditing && (
          <>
            <PageHeader.TitleArea>
              <PageHeader.Title as="h1" className={'lh-condensed'}>
                <VerifiedHTMLText className="f1 text-normal markdown-title" html={pullRequest.titleHtml} />
                <span className="d-inline-flex flex-items-center gap-1">
                  <span className="pl-2 fgColor-muted f1-light">#{pullRequest.number}</span>
                  {shouldShowLoggedInOptions && user.canEditTitle && (
                    <IconButton
                      icon={PencilIcon}
                      onClick={() => setIsEditing(true)}
                      size="medium"
                      aria-label="Edit title"
                      variant="invisible"
                    />
                  )}
                </span>
              </PageHeader.Title>
            </PageHeader.TitleArea>
            <PageHeader.Actions
              className={clsx(
                'flex-items-center gap-2 position-relative',
                showUpdatedHeader && !isFilesChangedRoute && 'd-none',
              )}
            >
              {isFilesChangedRoute && (
                <FilesChangedFeedback repository={repository} urls={urls} renderLocation={feedbackRenderLocation} />
              )}
              {shouldShowLoggedInOptions && (
                <div className="d-flex gap-2">
                  {validatedMergeData && !showUpdatedHeader && (
                    <MergeMethodContextProvider defaultMergeMethod={validatedMergeData.defaultMergeMethod}>
                      <MergeStatusButtonWrapper
                        channels={validatedMergeData.channels}
                        headSha={validatedMergeData.headSha}
                        helpUrl={validatedMergeData.helpUrl}
                        viewerLogin={validatedMergeData.viewerLogin}
                        isDockedMode={isDockedMode}
                        onToggleDockedPanel={handleToggleDockedMergePanel}
                        mergeStatusButtonRef={mergeStatusButtonRef}
                      />
                      {isDockedMode && (
                        <MergeStatusPanel
                          helpUrl={validatedMergeData.helpUrl}
                          viewerLogin={validatedMergeData.viewerLogin}
                          channels={validatedMergeData.channels}
                        />
                      )}
                    </MergeMethodContextProvider>
                  )}
                  {!showUpdatedHeader && (
                    <PullRequestCodeButton
                      codespacesEnabled={repository.codespacesEnabled}
                      headBranch={pullRequest.headBranch}
                      isEnterprise={repository.isEnterprise}
                      pullRequestId={pullRequest.id}
                      pullRequestNumber={pullRequest.number}
                      repository={repository}
                      size={headerButtonSize}
                    />
                  )}
                </div>
              )}
            </PageHeader.Actions>
          </>
        )}
        <PageHeader.Description className={'d-flex flex-column flex-items-start'}>
          <div
            className={clsx(
              'd-flex width-full',
              showUpdatedHeader
                ? 'flex-items-center gap-3 flex-wrap'
                : 'flex-items-start gap-2 flex-column flex-sm-row flex-justify-between',
            )}
          >
            <div className="d-flex flex-items-center gap-2 flex-shrink-0">
              <PullRequestStateLabel state={pullRequest.state} />
              <StackState pullRequestNumber={pullRequest.number} stack={stack} />
            </div>
            <div className={showUpdatedHeader ? undefined : 'flex-1 overflow-hidden'}>
              <PullRequestHeaderSummary
                author={pullRequest.author}
                mergedTime={pullRequest.mergedTime}
                baseBranch={pullRequest.baseBranch}
                baseRepositoryDefaultBranch={repository.defaultBranch}
                baseRepositoryName={repository.name}
                baseRepositoryOwnerLogin={repository.ownerLogin}
                canChangeBase={user.canChangeBase}
                commitsCount={pullRequest.commitsCount}
                headBranch={pullRequest.headBranch}
                headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                headRepositoryName={pullRequest.headRepositoryName}
                isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                isEditing={isEditing}
                mergedBy={pullRequest.mergedBy}
                mergedByAvatarUrl={pullRequest.mergedByAvatarUrl}
                mergedByName={pullRequest.mergedByName}
                repositoryId={repository.id}
                pullRequestId={pullRequest.id}
                pullRequestNumber={pullRequest.number}
                setIsEditing={setIsEditing}
                state={pullRequest.state}
                stack={stack}
              />
            </div>
          </div>
          <PullRequestBanners bannersData={bannersData} pullRequest={pullRequest} repository={repository} />
        </PageHeader.Description>
        <PageHeader.Navigation
          className={clsx(
            showUpdatedHeader ? 'tmp-pt-3 tmp-ml-n5 tmp-mr-n5 tmp-px-3' : 'tmp-pt-3 tmp-px-3 tmp-ml-n3 tmp-mr-n3',
            showUpdatedHeader && styles.navBorderBottom,
          )}
        >
          <div
            className={clsx(
              styles.diffStatesWrapper,
              showUpdatedHeader ? 'float-right d-none d-md-block tmp-mr-3 tmp-px-3' : 'float-right d-none d-md-block',
            )}
          >
            <ErrorBoundary boundaryName="PullRequestHeaderDiffStat" fallback={null}>
              <Suspense fallback={<DiffStatLoadingSkeleton />}>{!showUpdatedHeader && <DiffStat />}</Suspense>
            </ErrorBoundary>
          </div>
          <div className="flex-auto">
            <PullRequestHeaderNavigation
              activeRoute={activeRoute}
              pullRequest={pullRequest}
              repository={repository}
              urls={urls}
            />
          </div>
        </PageHeader.Navigation>
      </PageHeader>
      {!showUpdatedHeader && !isFilesChangedRoute && (
        <StickyPullRequestHeader
          repository={repository}
          pullRequest={pullRequest}
          pageVariant={stickyHeaderVariant ?? 'condensed'}
        />
      )}
    </>
  )
}
